import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { getJob, getUserJobs } from '../../../services/jobs';
import { UserContext } from '../../context/UserContext';
import { BaseContainer } from '../../global/Core';

import type { Job } from '../../../types/job';

const StyledCard = styled(Card)`
    max-width: 600px;
    margin: 0 auto;
    padding: 2rem;
    text-align: center;
`;

const StatusContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
`;

const StatusIcon = styled(Box)<{ $qualified: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: ${({ $qualified }) =>
        $qualified ? '#e8f5e8' : '#fdeaea'};

    svg {
        font-size: 40px;
        color: ${({ $qualified }) => ($qualified ? '#4caf50' : '#f44336')};
    }
`;

const ActionButton = styled(Button)`
    min-width: 200px;
    padding: 12px 24px;
    margin-top: 1rem;
`;

export default function JobQualifyCheck() {
    const { jobId } = useParams<{ jobId: string }>();
    const { user } = useContext(UserContext);
    const [job, setJob] = useState<Job | null>(null);
    const [loading, setLoading] = useState(true);
    const [qualified, setQualified] = useState<boolean>(false);
    const [hasAvailableSlots, setHasAvailableSlots] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (jobId && user?.id) {
            // First check if user is authenticated and is a candidate
            if (user?.levylRole !== 'candidate') {
                setError('Only candidates can qualify for job positions');
                setLoading(false);
                return;
            }

            // Fetch the specific job and check qualification
            Promise.all([getJob(parseInt(jobId, 10)), getUserJobs(user.id)])
                .then(([jobData, userJobs]) => {
                    setJob(jobData);

                    // Check if user qualifies for this specific job
                    // getUserJobs returns jobs that match user criteria AND have slots
                    // So we check if this job is in the returned list
                    const qualifiedJob = userJobs.find(
                        (userJob) => userJob.id === jobData.id
                    );

                    // If the job is not in userJobs, it could mean:
                    // 1. User doesn't qualify, OR
                    // 2. User qualifies but no slots are available
                    // We need to check if job has any available slots to distinguish
                    const availableInterviews =
                        jobData.interviews?.filter(
                            (interview) => interview.status === 'available'
                        ) || [];

                    setHasAvailableSlots(availableInterviews.length > 0);

                    // If job is in userJobs, user definitely qualifies
                    // If not in userJobs but has available slots, likely doesn't qualify
                    // If not in userJobs and no available slots, we assume qualified but no slots
                    if (qualifiedJob) {
                        setQualified(true);
                    } else if (availableInterviews.length > 0) {
                        // Job has slots but user didn't get it in qualified list
                        setQualified(false);
                    } else {
                        // No slots available - we can't determine qualification
                        // For now, assume not qualified to be safe
                        setQualified(false);
                    }

                    setLoading(false);
                })
                .catch(() => {
                    setError('Job not found or no longer available');
                    setLoading(false);
                });
        }
    }, [jobId, user?.id, user?.levylRole]);

    // Redirect if not authenticated
    if (!user?.id) {
        return <Redirect to={`/job/${jobId}/qualify`} />;
    }

    if (loading) {
        return (
            <BaseContainer>
                <StyledCard>
                    <CardContent>
                        <Typography variant={'h5'}>
                            {'Checking your qualification...'}
                        </Typography>
                    </CardContent>
                </StyledCard>
            </BaseContainer>
        );
    }

    if (error || !job) {
        return (
            <BaseContainer>
                <StyledCard>
                    <CardContent>
                        <Typography variant={'h5'} color={'error'} gutterBottom>
                            {'Error'}
                        </Typography>
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {error || 'Job not found or no longer available.'}
                        </Typography>
                        <ActionButton
                            variant={'outlined'}
                            onClick={() => window.history.back()}
                        >
                            {'Go Back'}
                        </ActionButton>
                    </CardContent>
                </StyledCard>
            </BaseContainer>
        );
    }

    const getStatusMessage = () => {
        if (qualified && hasAvailableSlots) {
            return {
                title: 'Great! You qualify for this position',
                message:
                    'Based on your profile, you meet the requirements for ' +
                    'this job. You can book an interview slot now.',
                action: 'Book Interview',
                actionUrl: `/secure/${user?.userID}/schedule/${jobId}`
            };
        }

        if (qualified && !hasAvailableSlots) {
            return {
                title: 'You qualify, but no slots available',
                message:
                    'You meet the requirements for this position, but ' +
                    'there are currently no available interview slots. ' +
                    'Check back later or explore other opportunities.',
                action: 'View Dashboard',
                actionUrl: `/secure/${user?.userID}/jobs`
            };
        }

        return {
            title: "You don't qualify for this position",
            message:
                "Based on your current profile, you don't meet all " +
                'the requirements for this job. Continue exploring other ' +
                'opportunities that might be a better fit.',
            action: 'View Dashboard',
            actionUrl: `/secure/${user?.userID}/candidate/dashboard`
        };
    };

    const status = getStatusMessage();
    const showQualified = qualified && hasAvailableSlots;

    return (
        <BaseContainer>
            <StyledCard>
                <CardContent>
                    <Typography variant={'h5'} component={'h1'} gutterBottom>
                        {job.title}
                    </Typography>

                    <Typography
                        variant={'body2'}
                        color={'text.secondary'}
                        gutterBottom
                    >
                        {job.company.name} {'\u2022'} {job.company.location}
                    </Typography>

                    <StatusContainer>
                        <StatusIcon $qualified={showQualified}>
                            {showQualified ? (
                                <CheckCircleIcon />
                            ) : (
                                <ErrorIcon />
                            )}
                        </StatusIcon>

                        <Typography
                            variant={'h6'}
                            component={'h2'}
                            gutterBottom
                        >
                            {status.title}
                        </Typography>

                        <Typography
                            variant={'body1'}
                            color={'text.secondary'}
                            sx={{ maxWidth: '400px' }}
                        >
                            {status.message}
                        </Typography>
                    </StatusContainer>

                    <ActionButton
                        variant={'contained'}
                        color={'primary'}
                        href={status.actionUrl}
                    >
                        {status.action}
                    </ActionButton>
                </CardContent>
            </StyledCard>
        </BaseContainer>
    );
}
